import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { adminHeatmapPoints, adminHeatmapStats, adminMarkAlert } from "../lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { EMERGENCY_FR } from "../i18n.jsx";
import { RefreshCw, Pause, Activity, MapPin, Clock, AlertTriangle, Layers, Eye, EyeOff, Flame, ShieldCheck } from "lucide-react";
import { getStyle, installBuildingExtrusion, applySatelliteZoomCap } from "../lib/mapStyles";
import { getCountryBoundary, installCountryMask } from "../lib/countryMask";
import { EMERGENCY_META, EMERGENCY_TYPES } from "./EmergencyTypeGrid";
import { useMapFullscreen, MapFullscreenButton } from "./MapFullscreenToggle";
import { MapStyleSwitcher } from "./map/MapStyleSwitcher";
import { useFacilities } from "../lib/useFacilities";
import { SERVICE_CATEGORIES, buildServicesGeoJSON } from "../data/maliEmergencyServices";
import { buildServiceIcon } from "../lib/mapPageHelpers";
import {
  CHART_MARGIN, CHART_TICK,
  CHART_TOOLTIP_CONTENT, CHART_TOOLTIP_LABEL, CHART_TOOLTIP_ITEM, CHART_TOOLTIP_FORMATTER,
  MALI_CENTRE, MALI_INIT_ZOOM,
  RANGES, REFRESH_OPTIONS, TYPE_META,
  fmtTime, escapeHtml, hasWebGL, buildGeoJSON,
  QUALITY_LABELS_FR, QUALITY_COLORS, fmtLocationFreshness,
} from "./heatmap/heatmapHelpers";

const SOURCE_ID = "alerts-src";
const HEAT_LAYER_ID = "alerts-heat";
const CLUSTER_LAYER_ID = "alerts-clusters";
const CLUSTER_COUNT_ID = "alerts-cluster-count";
const POINT_LAYER_ID = "alerts-points";
// Iter90 — GPS accuracy-radius ring, drawn under each single (non-
// clustered) alert point. Same interpolation curve as the citizen map's
// own "user-accuracy" ring (MapPage.jsx) for visual consistency.
const POINT_ACC_LAYER_ID = "alerts-points-accuracy";
// Iter85 — Emergency-services POI layer (hospitals/police/fire/FAMA/
// embassies), same icon set as the citizen map, always-on reference
// layer for dispatchers.
const ADMIN_SVC_SOURCE = "admin-emergency-services";
const ALL_SVC_KEYS = new Set(SERVICE_CATEGORIES.map((s) => s.key));

export const HeatmapCommandCenter = ({ focusAlert, onFocusConsumed, flashingAlerts = [], onAckAlert } = {}) => {
  // ---------- State ----------
  const [rangeId, setRangeId] = useState("24h");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [refreshId, setRefreshId] = useState("30s");
  const [activeTypes, setActiveTypes] = useState(
    Object.fromEntries(EMERGENCY_TYPES.map(({ key }) => [key, true]))
  );
  const [includeFalse, setIncludeFalse] = useState(false);
  const [showHeat, setShowHeat] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  // Iter92 — per-category POI visibility (hospitals/police/fire/FAMA/
  // embassies), same toggle UX as the citizen map's service pills.
  // Default: everything on, matching the previous "always on" behavior.
  const [activeServiceKeys, setActiveServiceKeys] = useState(() => new Set(ALL_SVC_KEYS));
  const [styleKind, setStyleKind] = useState("hybrid");
  const [points, setPoints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [webglOK] = useState(hasWebGL);

  // Iter85 — Live facility data (hospitals/police/fire/FAMA/embassies),
  // same source + cache as the citizen map.
  const { items: facilities } = useFacilities({ countries: "ML,GN" });

  // ---------- Refs ----------
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const didInitStyleRef = useRef(false);
  const hasFitRef = useRef(false);

  // ---------- Fullscreen toggle ----------
  // Lets EOC operators expand the heatmap to fill the entire browser
  // viewport when they want a wall-display view. Esc exits.
  const { isFs, toggle: toggleFs, fsWrapperClass } = useMapFullscreen(mapRef);

  // ---------- Filters → API params ----------
  const queryParams = useMemo(() => {
    const now = new Date();
    let since, until;
    if (rangeId === "custom") {
      since = customRange.from ? new Date(customRange.from).toISOString() : undefined;
      until = customRange.to ? new Date(customRange.to).toISOString() : undefined;
    } else {
      const r = RANGES.find((x) => x.id === rangeId) || RANGES[2];
      since = new Date(now.getTime() - r.ms).toISOString();
      until = now.toISOString();
    }
    const types = Object.entries(activeTypes).filter(([, v]) => v).map(([k]) => k).join(",");
    return { since, until, types, include_false: includeFalse, limit: 5000 };
  }, [rangeId, customRange, activeTypes, includeFalse]);

  // ---------- Data fetch ----------
  const fetchAll = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const [pts, st] = await Promise.all([
        adminHeatmapPoints(queryParams),
        adminHeatmapStats(queryParams),
      ]);
      setPoints(pts.points || []);
      setStats(st);
      setLastUpdated(new Date());
    } catch (e) {
      // Avoid spamming toasts during auto-refresh; only fire once on first load
      if (showSpinner) toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [queryParams]);

  // Initial load + refetch when filters change
  useEffect(() => {
    Promise.resolve().then(() => fetchAll(true));
    hasFitRef.current = false; // refit bounds for new filter
  }, [fetchAll]);

  // Auto-refresh polling
  useEffect(() => {
    const opt = REFRESH_OPTIONS.find((x) => x.id === refreshId);
    if (!opt || !opt.ms) return undefined;
    const id = setInterval(() => fetchAll(false), opt.ms);
    return () => clearInterval(id);
  }, [refreshId, fetchAll]);

  // ---------- Map init ----------
  useEffect(() => {
    if (!webglOK || !containerRef.current || mapRef.current) return undefined;
    didInitStyleRef.current = false;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getStyle(styleKind),
      center: [MALI_CENTRE.lng, MALI_CENTRE.lat],
      zoom: MALI_INIT_ZOOM,
      pitch: 0,
      bearing: 0,
      maxPitch: 60,
      attributionControl: false,
      pitchWithRotate: true,
      dragRotate: true,
    });
    // Iter85 — Native-resolution cap for Esri Satellite (see mapStyles.js).
    applySatelliteZoomCap(map, styleKind);
    // Iter78 — Parity with Users Map: tilt/compass moves to bottom-left.
    map.addControl(new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }), "bottom-left");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;
    try {
      if (localStorage.getItem("rr_poi_debug") === "1") window.__rrCarteMap = map;
    } catch (_e) { /* noop */ }

    const ro = new ResizeObserver(() => { try { map.resize(); } catch (_e) { /* noop */ } });
    ro.observe(containerRef.current);

    return () => { ro.disconnect(); map.remove(); mapRef.current = null; didInitStyleRef.current = false; };
  }, [webglOK]);

  // ---------- Layer install (idempotent) ----------
  const installLayers = useCallback((map) => {
    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: buildGeoJSON(points),
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 50,
      });
    }

    if (!map.getLayer(HEAT_LAYER_ID)) {
      map.addLayer({
        id: HEAT_LAYER_ID,
        type: "heatmap",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        layout: { visibility: showHeat ? "visible" : "none" },
        paint: {
          "heatmap-weight": 1,
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, 13, 4],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 6, 9, 24, 14, 44],
          "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 0, 0.85, 16, 0.3],
          "heatmap-color": [
            "interpolate", ["linear"], ["heatmap-density"],
            0, "rgba(34,211,238,0)",
            0.2, "rgba(34,211,238,0.55)",
            0.45, "rgba(250,204,21,0.7)",
            0.7, "rgba(249,115,22,0.85)",
            1, "rgba(239,68,68,0.95)",
          ],
        },
      });
    }

    if (!map.getLayer(CLUSTER_LAYER_ID)) {
      map.addLayer({
        id: CLUSTER_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: { visibility: showMarkers ? "visible" : "none" },
        paint: {
          "circle-color": [
            "step", ["get", "point_count"],
            "#22D3EE", 10, "#FACC15", 50, "#F97316", 200, "#EF4444",
          ],
          "circle-radius": [
            "step", ["get", "point_count"],
            16, 10, 22, 50, 28, 200, 36,
          ],
          "circle-stroke-color": "#0b0f14",
          "circle-stroke-width": 2,
          "circle-opacity": 0.92,
        },
      });
    }

    if (!map.getLayer(CLUSTER_COUNT_ID)) {
      map.addLayer({
        id: CLUSTER_COUNT_ID,
        type: "symbol",
        source: SOURCE_ID,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": 13,
          "text-allow-overlap": true,
          visibility: showMarkers ? "visible" : "none",
        },
        paint: {
          "text-color": "#0b0f14",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.4,
        },
      });
    }

    if (!map.getLayer(POINT_ACC_LAYER_ID)) {
      map.addLayer({
        id: POINT_ACC_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        layout: { visibility: showMarkers ? "visible" : "none" },
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["coalesce", ["get", "accuracy"], 0], 0, 0, 20, 16, 100, 34, 500, 60],
          "circle-color": ["get", "color"],
          "circle-opacity": 0.12,
          "circle-stroke-color": ["get", "color"],
          "circle-stroke-width": 1,
          "circle-stroke-opacity": 0.4,
        },
      });
    }

    if (!map.getLayer(POINT_LAYER_ID)) {
      map.addLayer({
        id: POINT_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        layout: { visibility: showMarkers ? "visible" : "none" },
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 4, 8, 6, 14, 9],
          "circle-color": ["get", "color"],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.2,
        },
      });

      // Cluster click → zoom in
      map.on("click", CLUSTER_LAYER_ID, async (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const clusterId = f.properties.cluster_id;
        const src = map.getSource(SOURCE_ID);
        try {
          const z = await src.getClusterExpansionZoom(clusterId);
          map.easeTo({ center: f.geometry.coordinates, zoom: Math.min(z, 16), duration: 500 });
        } catch (_e) { /* noop */ }
      });
      map.on("mouseenter", CLUSTER_LAYER_ID, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", CLUSTER_LAYER_ID, () => { map.getCanvas().style.cursor = ""; });

      // Single-point popup
      map.on("mouseenter", POINT_LAYER_ID, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", POINT_LAYER_ID, () => { map.getCanvas().style.cursor = ""; });
      map.on("click", POINT_LAYER_ID, (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties;
        const [lng, lat] = f.geometry.coordinates;
        const quality = p.location_quality || "UNKNOWN";
        const qColor = QUALITY_COLORS[quality] || "#71717a";
        const qLabel = QUALITY_LABELS_FR[quality] || "Inconnue";
        const freshness = fmtLocationFreshness(p.location_updated_at || p.ts);
        const freshColor = freshness.tier === "live" ? "#10B981" : freshness.tier === "recent" ? "#F59E0B" : "#9CA3AF";
        const html = `
          <div data-testid="map-popup-${escapeHtml(p.id)}" style="min-width:220px;font-family:inherit;color:#e5e7eb">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px">
              <div style="font-size:13px;font-weight:900;letter-spacing:0.04em;text-transform:uppercase">${escapeHtml(p.type)}</div>
              <div style="display:flex;gap:4px">
                ${p.flagged === "true" ? '<span style="background:#CA8A04;color:#fff;padding:2px 4px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Flag</span>' : ""}
                ${p.is_false === "true" ? '<span style="background:#EF4444;color:#fff;padding:2px 4px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em">Fausse</span>' : ""}
              </div>
            </div>
            <div style="font-family:ui-monospace,monospace;font-size:11px;color:#a1a1aa">${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
            <div style="font-size:11px;color:#a1a1aa;margin-top:2px">${escapeHtml(fmtTime(p.ts))}</div>
            <div style="display:flex;align-items:center;gap:8px;margin-top:6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em">
              <span style="color:${qColor}">● ${p.accuracy != null ? `±${Math.round(p.accuracy)} m` : "Précision inconnue"} · ${escapeHtml(qLabel)}</span>
              <span style="color:${freshColor}">${escapeHtml(freshness.label)}</span>
            </div>
            <div style="font-size:11px;color:#e5e7eb;margin-top:6px">
              <span style="color:#a1a1aa">Reporter: </span>
              <span style="font-family:ui-monospace,monospace;font-weight:700">${escapeHtml(p.reporter_phone)}</span>
            </div>
            ${p.description ? `<p style="font-size:11px;color:#cbd5e1;margin-top:4px;line-height:1.4">${escapeHtml(p.description)}</p>` : ""}
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;margin-top:8px">
              <button data-mark-id="${escapeHtml(p.id)}" data-mark-false="true"  data-testid="map-mark-false-${escapeHtml(p.id)}" style="border:1px solid #EF4444;color:#EF4444;background:transparent;padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;cursor:pointer">Fausse</button>
              <button data-mark-id="${escapeHtml(p.id)}" data-mark-false="false" data-testid="map-mark-true-${escapeHtml(p.id)}" style="border:1px solid #22C55E;color:#22C55E;background:transparent;padding:6px 8px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;cursor:pointer">Légitime</button>
            </div>
          </div>`;
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new maplibregl.Popup({ closeButton: true, offset: 12, className: "rr-cmd-popup" })
          .setLngLat([lng, lat])
          .setHTML(html)
          .addTo(map);
        const node = popupRef.current.getElement();
        node?.querySelectorAll("button[data-mark-id]").forEach((btn) => {
          btn.addEventListener("click", async () => {
            const id = btn.getAttribute("data-mark-id");
            const isFalse = btn.getAttribute("data-mark-false") === "true";
            setBusyId(id);
            try {
              await adminMarkAlert(id, isFalse);
              toast.success(isFalse ? "Marquée comme fausse" : "Marquée comme légitime");
              popupRef.current?.remove();
              fetchAll(false);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Action impossible");
            } finally { setBusyId(null); }
          });
        });
      });
    }
  }, []);

  // ── Install emergency-services POI layer (hospitals/police/fire/FAMA/
  // embassies) — same real-glyph icons as the citizen map (MapPage.jsx),
  // always visible for dispatchers as a geographic reference layer.
  const installServiceLayers = useCallback((map) => {
    if (map.getSource(ADMIN_SVC_SOURCE)) return;
    // Iter86 — Native clustering (same recipe as the alerts source above)
    // so dense zones like Bamako collapse overlapping POI icons into a
    // numbered badge; tapping a badge zooms in until it breaks apart.
    map.addSource(ADMIN_SVC_SOURCE, {
      type: "geojson",
      data: buildServicesGeoJSON(activeServiceKeys, facilities),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
    SERVICE_CATEGORIES.forEach((s) => {
      const imgId = `admin-svc-icon-${s.key}`;
      if (!map.hasImage(imgId)) {
        map.addImage(imgId, buildServiceIcon(s.key, s.color, 64), { pixelRatio: 2 });
      }
    });
    // Deliberately dark-navy (not the alert palette's cyan/yellow/red) so
    // dispatchers never mistake a POI cluster for an alert cluster.
    map.addLayer({
      id: "admin-svc-clusters",
      type: "circle",
      source: ADMIN_SVC_SOURCE,
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": ["step", ["get", "point_count"], 16, 5, 20, 15, 24],
        "circle-color": "#0F1428",
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2.5,
      },
    });
    map.addLayer({
      id: "admin-svc-cluster-count",
      type: "symbol",
      source: ADMIN_SVC_SOURCE,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": 12,
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#ffffff" },
    });
    map.addLayer({
      id: "admin-svc-bg",
      type: "symbol",
      source: ADMIN_SVC_SOURCE,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": ["concat", "admin-svc-icon-", ["get", "category"]],
        "icon-size": 0.85,
        "icon-allow-overlap": true,
        "icon-anchor": "center",
      },
    });
    map.addLayer({
      id: "admin-svc-label",
      type: "symbol",
      source: ADMIN_SVC_SOURCE,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-field": ["get", "name"],
        "text-size": 9,
        "text-offset": [0, 1.4],
        "text-anchor": "top",
        "text-allow-overlap": false,
        "text-optional": true,
      },
      paint: {
        "text-color": "#e5e7eb",
        "text-halo-color": "#0b0f14",
        "text-halo-width": 1.3,
      },
    });

    // Tap a POI cluster badge to zoom in until it breaks apart.
    map.on("click", "admin-svc-clusters", async (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const clusterId = f.properties.cluster_id;
      const src = map.getSource(ADMIN_SVC_SOURCE);
      try {
        const z = await src.getClusterExpansionZoom(clusterId);
        map.easeTo({ center: f.geometry.coordinates, zoom: z + 0.5, duration: 500 });
      } catch (_e) { /* noop */ }
    });
    map.on("mouseenter", "admin-svc-clusters", () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", "admin-svc-clusters", () => { map.getCanvas().style.cursor = ""; });
  }, [facilities, activeServiceKeys]);

  // Install on initial style load
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const onLoad = () => {
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, styleKind);
        installServiceLayers(map);
        installLayers(map);
      });
    };
    if (map.isStyleLoaded()) onLoad();
    else map.once("load", onLoad);
    return () => { map.off("load", onLoad); };
  }, [installLayers, installServiceLayers]);

  // Re-style switch
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!didInitStyleRef.current) { didInitStyleRef.current = true; return; }
    map.setStyle(getStyle(styleKind));
    applySatelliteZoomCap(map, styleKind);
    map.once("styledata", () => {
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, styleKind);
        installServiceLayers(map);
        installLayers(map);
      });
    });
  }, [styleKind, installLayers, installServiceLayers]);

  // Push fresh data
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource(SOURCE_ID);
      if (src) src.setData(buildGeoJSON(points));
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);

    // Fit bounds once per filter change
    if (!hasFitRef.current && points.length > 0) {
      hasFitRef.current = true;
      const bounds = new maplibregl.LngLatBounds();
      points.forEach((p) => bounds.extend([p.lng, p.lat]));
      if (!bounds.isEmpty()) {
        const fit = () => map.fitBounds(bounds, { padding: 60, maxZoom: 11, duration: 600 });
        if (map.isStyleLoaded()) fit();
        else map.once("idle", fit);
      }
    }
  }, [points]);

  // Toggle visibility helpers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(HEAT_LAYER_ID)) return;
    map.setLayoutProperty(HEAT_LAYER_ID, "visibility", showHeat ? "visible" : "none");
  }, [showHeat]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    [POINT_LAYER_ID, POINT_ACC_LAYER_ID, CLUSTER_LAYER_ID, CLUSTER_COUNT_ID].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", showMarkers ? "visible" : "none");
    });
  }, [showMarkers]);

  // ── Sync service-icons layer when live facility list OR the
  // per-category toggle selection changes ────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource(ADMIN_SVC_SOURCE);
    if (src) src.setData(buildServicesGeoJSON(activeServiceKeys, facilities));
  }, [facilities, activeServiceKeys]);

  const toggleService = (key) => {
    setActiveServiceKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };


  // ── Focus on an alert when admin clicks its GPS in the Alertes list ──
  // The parent passes a `focusAlert={id,lat,lng,color,label,created_at,description}` prop;
  // we flyTo it, drop a temporary red highlight ring, and open a popup with the
  // alert details. After consuming we clear the prop so re-mount doesn't re-trigger.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusAlert) return undefined;
    const { lat, lng, color, label, created_at, description, id } = focusAlert;
    if (lat == null || lng == null) {
      onFocusConsumed?.();
      return undefined;
    }
    const HL_SRC = "admin-focus-alert";
    const apply = () => {
      try {
        const fc = {
          type: "FeatureCollection",
          features: [{
            type: "Feature",
            properties: {},
            geometry: { type: "Point", coordinates: [lng, lat] },
          }],
        };
        let src = map.getSource(HL_SRC);
        if (!src) {
          map.addSource(HL_SRC, { type: "geojson", data: fc });
          map.addLayer({
            id: "admin-focus-alert-halo",
            type: "circle",
            source: HL_SRC,
            paint: {
              "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 26, 16, 60],
              "circle-color": color || "#DC2626",
              "circle-opacity": 0.22,
              "circle-blur": 0.6,
            },
          });
          map.addLayer({
            id: "admin-focus-alert-ring",
            type: "circle",
            source: HL_SRC,
            paint: {
              "circle-radius": 14,
              "circle-color": "rgba(0,0,0,0)",
              "circle-stroke-color": color || "#DC2626",
              "circle-stroke-width": 3,
            },
          });
          map.addLayer({
            id: "admin-focus-alert-dot",
            type: "circle",
            source: HL_SRC,
            paint: {
              "circle-radius": 8,
              "circle-color": color || "#DC2626",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2.5,
            },
          });
        } else {
          src.setData(fc);
        }
        const easing = (t) => 1 - Math.pow(1 - t, 4);
        map.flyTo({ center: [lng, lat], zoom: 16, pitch: 45, speed: 1.2, curve: 1.5, essential: true, easing });
        const ts = created_at ? new Date(created_at).toLocaleString("fr-FR") : "";
        const desc = (description || "").slice(0, 180);
        new maplibregl.Popup({ closeButton: true, maxWidth: "260px", offset: 14 })
          .setLngLat([lng, lat])
          .setHTML(`
            <div style="font-family:inherit;min-width:200px;color:#e5e7eb">
              <div style="font-size:13px;font-weight:800;color:${escapeHtml(color || "#DC2626")}">${escapeHtml(label || "Alerte")}</div>
              <div style="font-size:11px;color:#a1a1aa;margin-top:2px">${escapeHtml(ts)}</div>
              ${desc ? `<div style="font-size:11px;margin-top:6px;color:#e5e7eb;line-height:1.4">${escapeHtml(desc)}</div>` : ""}
              <div style="font-family:ui-monospace,monospace;font-size:10px;color:#71717a;margin-top:6px">${lat.toFixed(5)}, ${lng.toFixed(5)}</div>
            </div>`)
          .addTo(map);
        onFocusConsumed?.();
      } catch (_e) { /* noop */ }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("styledata", apply);
    return undefined;
  }, [focusAlert?.id]);

  // ────────────────────────────────────────────────────────────────
  // Real-time firehose flash layer: every unacknowledged alert is
  // rendered as a bright red pulsing ring on top of the regular
  // heat / cluster / point layers. Updates whenever `flashingAlerts`
  // changes (driven by the WS firehose hook).
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const SRC = "admin-firehose-flash";
    const RING1 = "admin-firehose-flash-ring1";
    const RING2 = "admin-firehose-flash-ring2";
    const DOT = "admin-firehose-flash-dot";
    let pulseRaf = null;

    const buildFc = () => ({
      type: "FeatureCollection",
      features: (flashingAlerts || [])
        .filter((a) => a && a.coordinates && typeof a.coordinates.latitude === "number")
        .map((a) => ({
          type: "Feature",
          properties: { id: a.id, type: a.emergency_type, color: "#EF4444" },
          geometry: { type: "Point", coordinates: [a.coordinates.longitude, a.coordinates.latitude] },
        })),
    });

    const apply = () => {
      try {
        const fc = buildFc();
        let src = map.getSource(SRC);
        if (!src) {
          map.addSource(SRC, { type: "geojson", data: fc });
          map.addLayer({
            id: RING1, type: "circle", source: SRC,
            paint: {
              "circle-radius": 18,
              "circle-color": "rgba(0,0,0,0)",
              "circle-stroke-color": "#EF4444",
              "circle-stroke-width": 2.5,
              "circle-stroke-opacity": 0.85,
            },
          });
          map.addLayer({
            id: RING2, type: "circle", source: SRC,
            paint: {
              "circle-radius": 12,
              "circle-color": "rgba(0,0,0,0)",
              "circle-stroke-color": "#EF4444",
              "circle-stroke-width": 2,
              "circle-stroke-opacity": 0.6,
            },
          });
          map.addLayer({
            id: DOT, type: "circle", source: SRC,
            paint: {
              "circle-radius": 7,
              "circle-color": "#EF4444",
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });
          const onClick = (e) => {
            const f = e.features && e.features[0];
            if (!f) return;
            const id = f.properties.id;
            const [lng, lat] = f.geometry.coordinates;
            const popup = new maplibregl.Popup({ closeButton: true, maxWidth: "240px", offset: 14 })
              .setLngLat([lng, lat])
              .setHTML(`
                <div style="font-family:inherit;color:#e5e7eb">
                  <div style="font-size:12px;font-weight:800;color:#EF4444;text-transform:uppercase;letter-spacing:0.18em">Nouvelle alerte</div>
                  <div style="font-size:11px;color:#a1a1aa;margin-top:2px;font-family:ui-monospace,monospace">${id}</div>
                  <button id="ack-${id}" style="margin-top:8px;padding:6px 10px;background:#06b6d4;color:#000;border:0;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;font-size:10px;cursor:pointer;width:100%">Acquitter</button>
                </div>`)
              .addTo(map);
            requestAnimationFrame(() => {
              const btn = document.getElementById(`ack-${id}`);
              if (btn) {
                btn.addEventListener("click", () => {
                  try { onAckAlert?.(id); } catch (e2) { void e2; }
                  popup.remove();
                });
              }
            });
          };
          map.on("click", DOT, onClick);
          map.on("click", RING1, onClick);
          map.on("mouseenter", DOT, () => { map.getCanvas().style.cursor = "pointer"; });
          map.on("mouseleave", DOT, () => { map.getCanvas().style.cursor = ""; });
        } else {
          src.setData(fc);
        }
        const animate = () => {
          if (!mapRef.current) return;
          const t = (Date.now() % 1400) / 1400;
          const r1 = 14 + 18 * t;
          const r2 = 8 + 14 * ((t + 0.5) % 1);
          const o1 = 0.85 * (1 - t);
          const o2 = 0.55 * (1 - ((t + 0.5) % 1));
          try {
            if (map.getLayer(RING1)) {
              map.setPaintProperty(RING1, "circle-radius", r1);
              map.setPaintProperty(RING1, "circle-stroke-opacity", o1);
            }
            if (map.getLayer(RING2)) {
              map.setPaintProperty(RING2, "circle-radius", r2);
              map.setPaintProperty(RING2, "circle-stroke-opacity", o2);
            }
          } catch (_e) { /* noop */ }
          pulseRaf = requestAnimationFrame(animate);
        };
        if (fc.features.length > 0 && pulseRaf == null) {
          pulseRaf = requestAnimationFrame(animate);
        }
      } catch (_e) { /* noop */ }
    };

    if (map.isStyleLoaded()) apply();
    else map.once("styledata", apply);

    return () => {
      if (pulseRaf) cancelAnimationFrame(pulseRaf);
    };
  }, [flashingAlerts]);


  // ---------- Render ----------
  if (!webglOK) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-[#0b0f14] text-zinc-200" data-testid="webgl-fallback">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" />
          <h2 className="font-display text-xl font-black tracking-tight">Carte indisponible</h2>
          <p className="text-sm text-zinc-400">
            Votre navigateur ne supporte pas WebGL — nécessaire pour afficher la carte interactive.
            Mettez à jour votre navigateur, activez l’accélération matérielle, ou utilisez Chrome/Edge récents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#0b0f14] text-zinc-200" data-testid="admin-map-tab">
      {/* COMMAND BAR */}
      <CommandBar
        rangeId={rangeId} setRangeId={setRangeId}
        customRange={customRange} setCustomRange={setCustomRange}
        refreshId={refreshId} setRefreshId={setRefreshId}
        activeTypes={activeTypes} setActiveTypes={setActiveTypes}
        includeFalse={includeFalse} setIncludeFalse={setIncludeFalse}
        showHeat={showHeat} setShowHeat={setShowHeat}
        showMarkers={showMarkers} setShowMarkers={setShowMarkers}
        activeServiceKeys={activeServiceKeys} toggleService={toggleService}
        styleKind={styleKind} setStyleKind={setStyleKind}
        onRefresh={() => fetchAll(true)}
        loading={loading}
        lastUpdated={lastUpdated}
        total={stats?.total || 0}
      />

      {/* MAP + STATS */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div
          className={fsWrapperClass("relative w-full h-[55vh] lg:h-auto lg:flex-1 lg:min-h-0")}
          data-testid="cmd-map-container"
        >
          <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} />
          {/* Fullscreen toggle — top-right, slightly inset so the
              MapLibre attribution/compass controls don't collide. */}
          <MapFullscreenButton
            isFs={isFs}
            onToggle={toggleFs}
            className="absolute top-3 right-3 z-[450]"
          />
          {/* Live status pill */}
          <div className="absolute top-3 left-3 z-[400] flex items-center gap-2 bg-[#111418]/90 backdrop-blur border border-zinc-800 px-3 py-1.5">
            <span className={`w-2 h-2 rounded-full ${refreshId !== "off" ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"}`} />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300">
              {refreshId !== "off" ? `Live · ${REFRESH_OPTIONS.find((r) => r.id === refreshId).label}` : "Pause"}
            </span>
            {lastUpdated && (
              <span className="text-[10px] font-mono text-zinc-500" data-testid="last-updated">
                {lastUpdated.toLocaleTimeString("fr-FR")}
              </span>
            )}
          </div>

          {/* Iter85 — Traffic/Incidents/Accidents toggles + Densité legend
              removed per admin feedback (cluttered the tactical view). */}
        </div>
        <StatsSidebar stats={stats} loading={loading} />
      </div>
      {busyId && <span className="hidden" aria-hidden="true">{busyId}</span>}
    </div>
  );
};

// ---------------------- Subcomponents ----------------------

const CommandBar = ({
  rangeId, setRangeId, customRange, setCustomRange,
  refreshId, setRefreshId,
  activeTypes, setActiveTypes,
  includeFalse, setIncludeFalse,
  showHeat, setShowHeat, showMarkers, setShowMarkers,
  activeServiceKeys, toggleService,
  styleKind, setStyleKind,
  onRefresh, loading, lastUpdated, total,
}) => {
  return (
    <div className="border-b border-zinc-800 bg-[#0f1318]" data-testid="cmd-bar">
      {/* Top row: title + counters */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
            Heat-map · centre opérationnel
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[10px] text-zinc-500 font-mono hidden sm:block">
            {lastUpdated ? `MAJ ${lastUpdated.toLocaleTimeString("fr-FR")}` : "—"}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400" data-testid="cmd-total">
            {total} alertes
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            data-testid="cmd-refresh-btn"
            className="border border-zinc-700 hover:border-emerald-500 text-zinc-300 hover:text-emerald-400 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Middle row: time presets + auto-refresh */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800 overflow-x-auto cmd-scroll-row">
        <Clock className="shrink-0 w-3.5 h-3.5 text-zinc-500" />
        <div className="flex items-center gap-1 shrink-0" data-testid="cmd-range-presets">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRangeId(r.id)}
              data-testid={`cmd-range-${r.id}`}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                rangeId === r.id ? "bg-emerald-500 text-black border-emerald-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
              }`}
            >{r.label}</button>
          ))}
          <button
            type="button"
            onClick={() => setRangeId("custom")}
            data-testid="cmd-range-custom"
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
              rangeId === "custom" ? "bg-emerald-500 text-black border-emerald-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
            }`}
          >Custom</button>
        </div>
        {rangeId === "custom" && (
          <div className="flex items-center gap-1 text-[10px] text-zinc-400" data-testid="cmd-custom-range">
            <input
              type="datetime-local"
              value={customRange.from}
              onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
              data-testid="cmd-custom-from"
              className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-2 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
            />
            <span>→</span>
            <input
              type="datetime-local"
              value={customRange.to}
              onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
              data-testid="cmd-custom-to"
              className="bg-[#0b0f14] border border-zinc-700 text-zinc-200 px-2 py-1 text-[10px] font-mono focus:border-emerald-500 focus:outline-none"
            />
          </div>
        )}

        <span className="shrink-0 mx-2 h-4 w-px bg-zinc-800" />

        <div className="flex items-center gap-1 shrink-0" data-testid="cmd-refresh-presets">
          <span className="text-[10px] text-zinc-500 mr-1">Auto</span>
          {REFRESH_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRefreshId(r.id)}
              data-testid={`cmd-refresh-${r.id}`}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                refreshId === r.id ? "bg-cyan-500 text-black border-cyan-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
              }`}
            >
              {r.id === "off" ? <Pause className="w-3 h-3" /> : r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom row: type chips + view toggles. On mobile we let the row scroll
          horizontally (touch-pan) so all 8 categories remain reachable. */}
      <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto cmd-scroll-row">
        <div className="flex items-center gap-1 shrink-0" data-testid="cmd-type-chips">
          {Object.entries(TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon || ShieldCheck;
            const on = activeTypes[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTypes((s) => ({ ...s, [key]: !s[key] }))}
                data-testid={`cmd-type-${key.toLowerCase()}`}
                className={`shrink-0 flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                  on ? "border-transparent text-black" : "border-zinc-700 text-zinc-500 hover:text-zinc-200"
                }`}
                style={on ? { background: meta.color } : undefined}
              >
                <Icon className="w-3 h-3" />
                {EMERGENCY_FR[key]?.label || key}
              </button>
            );
          })}
        </div>

      </div>

      {/* View toggles row — kept on its own line so it never competes with
          the scrollable type-chips row above on small screens. */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-zinc-800/60 overflow-x-auto cmd-scroll-row">
        <button
          type="button"
          onClick={() => setIncludeFalse(!includeFalse)}
          data-testid="cmd-include-false"
          className={`shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
            includeFalse ? "bg-rose-500 text-black border-rose-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
          }`}
        >
          Inclure fausses
        </button>

        <span className="shrink-0 mx-1 h-4 w-px bg-zinc-800" />

        <button
          type="button"
          onClick={() => setShowHeat(!showHeat)}
          data-testid="heatmap-toggle"
          className={`shrink-0 flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
            showHeat ? "bg-orange-500 text-black border-orange-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <Flame className="w-3 h-3" /> Heat
        </button>
        <button
          type="button"
          onClick={() => setShowMarkers(!showMarkers)}
          data-testid="cmd-toggle-markers"
          className={`shrink-0 flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
            showMarkers ? "bg-cyan-500 text-black border-cyan-500" : "border-zinc-700 text-zinc-400 hover:text-zinc-200"
          }`}
        >
          {showMarkers ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Pins
        </button>
        {/* Iter85 — 3-way style selector (Plan/Satellite/Sombre), same
            pattern as the citizen map's layer popover. */}
        <MapStyleSwitcher
          styleKind={styleKind}
          onChange={setStyleKind}
          align="left"
          icon={Layers}
          toggleTestId="admin-map-layer-toggle"
          backdropTestId="admin-map-layer-menu-backdrop"
          menuTestId="admin-map-layer-menu"
          optionTestId={(key) => `admin-map-layer-${key}`}
        />
      </div>

      {/* Iter92 — per-category POI toggles (hospitals/police/fire/FAMA/
          embassies), same toggle UX as the citizen map's service pills —
          own row so it never competes with the type-chips / view-toggles
          rows above on small screens. */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-t border-zinc-800/60 overflow-x-auto cmd-scroll-row" data-testid="admin-svc-pills">
        <Layers className="shrink-0 w-3.5 h-3.5 text-zinc-500" />
        {SERVICE_CATEGORIES.map((s) => {
          const active = activeServiceKeys.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggleService(s.key)}
              data-testid={`admin-svc-toggle-${s.key}`}
              aria-pressed={active}
              className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border transition-colors ${
                active ? "text-black" : "border-zinc-700 text-zinc-500 hover:text-zinc-200"
              }`}
              style={active ? { background: s.color, borderColor: s.color } : undefined}
            >
              {s.short}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const StatsSidebar = ({ stats, loading }) => {
  const total = stats?.total || 0;
  const byType = stats?.by_type || { Medical: 0, Fire: 0, Police: 0, FAMA: 0 };
  // Iter77 — merge legacy emergency-type keys into their modern
  // equivalent so the "PAR CATÉGORIE" panel doesn't render two rows
  // both labelled "Enlèvement" (Missing + legacy Kidnapping) or two
  // rows both labelled "Sécurité" (Securite + legacy Security). We
  // dedupe by their French display label and keep the modern key's
  // metadata / icon.
  const LEGACY_ALIAS = {
    Kidnapping: "Missing",
    Security:   "Securite",
  };
  const mergedByType = React.useMemo(() => {
    const out = {};
    Object.entries(byType).forEach(([k, v]) => {
      const target = LEGACY_ALIAS[k] || k;
      out[target] = (out[target] || 0) + (Number(v) || 0);
    });
    return out;
  }, [byType]);
  const top = stats?.top_regions || [];
  const trend = stats?.trend || [];
  const peak = trend.reduce((acc, b) => (b.count > acc.count ? b : acc), { count: 0, label: "—" });

  return (
    <aside
      className="lg:w-[340px] border-t lg:border-t-0 lg:border-l border-zinc-800 bg-[#0f1318] overflow-y-auto"
      data-testid="cmd-stats-sidebar"
    >
      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500">Total alertes</div>
        <div className="font-display text-4xl font-black tracking-tight text-white" data-testid="cmd-total-stat">
          {loading ? "…" : total}
        </div>
        {peak.count > 0 && (
          <div className="text-[10px] font-mono text-zinc-500 mt-1">
            Pic : <span className="text-emerald-400">{peak.count}</span> @ {peak.label}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-2">Par catégorie</div>
        <div className="space-y-1.5">
          {Object.entries(mergedByType).map(([k, v]) => {
            const meta = TYPE_META[k] || {};
            const pct = total > 0 ? Math.round((v / total) * 100) : 0;
            const Icon = meta.icon || ShieldCheck;
            return (
              <div key={k} className="space-y-0.5" data-testid={`cmd-cat-${k.toLowerCase()}`}>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1.5 text-zinc-300">
                    <Icon className="w-3 h-3" style={{ color: meta.color }} />
                    {EMERGENCY_FR[k]?.label || k}
                  </span>
                  <span className="font-mono text-zinc-400">
                    <span className="text-white font-bold">{v}</span> · {pct}%
                  </span>
                </div>
                <div className="h-1 bg-zinc-800">
                  <div className="h-full" style={{ width: `${pct}%`, background: meta.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-2">Top zones actives</div>
        {top.length === 0 ? (
          <div className="text-xs text-zinc-500">Aucune donnée</div>
        ) : (
          <ol className="space-y-1">
            {top.map((r, i) => {
              const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
              return (
                <li key={r.region} className="flex items-center gap-2 text-[11px]" data-testid={`cmd-region-${i}`}>
                  <span className="font-mono text-zinc-500 w-4">{i + 1}.</span>
                  <MapPin className="w-3 h-3 text-cyan-400" />
                  <span className="flex-1 text-zinc-200 font-medium truncate">{r.region}</span>
                  <span className="font-mono text-zinc-400">
                    <span className="text-white font-bold">{r.count}</span>
                    <span className="text-zinc-600"> · {pct}%</span>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="px-4 py-3">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-2">Tendance</div>

        {trend.length === 0 ? (
          <div className="text-xs text-zinc-500">Aucune donnée</div>
        ) : (
          <div className="h-32 min-h-[128px]" data-testid="cmd-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke="#1f2937" vertical={false} />
                <XAxis dataKey="label" tick={CHART_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} width={20} allowDecimals={false} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_CONTENT}
                  labelStyle={CHART_TOOLTIP_LABEL}
                  itemStyle={CHART_TOOLTIP_ITEM}
                  formatter={CHART_TOOLTIP_FORMATTER}
                />
                <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#trendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </aside>
  );
};
